import React, { useState } from 'react';
import { Loader2, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from './ui/dialog';
import { toast } from './ui/sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Lead-gated download: asks name + email, records the lead, then downloads the asset.
const DownloadGate = ({ asset, assetLabel, downloadPath, children, testid }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !email.includes('@')) {
      toast.error('Please add your name and a valid email.');
      return;
    }
    setBusy(true);
    try {
      await fetch(`${BACKEND}/api/download-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, asset }),
      });
      // trigger the download
      const a = document.createElement('a');
      a.href = `${BACKEND}${downloadPath}`;
      a.setAttribute('download', '');
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Your download is starting: ${assetLabel}.`);
      setOpen(false);
      setName('');
      setEmail('');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const fieldClass =
    'w-full bg-transparent border-b border-[var(--border)] focus:border-[var(--accent)] outline-none py-3 font-mono  text-[var(--text)] placeholder:text-[var(--muted-2)] transition-colors';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        data-testid="download-gate-modal"
        className="bg-[var(--bg-2)] border border-[var(--border-strong)] text-[var(--text)] rounded-2xl sm:rounded-2xl sm:max-w-[460px]"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-medium text-[1.6rem] tracking-[-0.01em]">
            {assetLabel}
          </DialogTitle>
          <DialogDescription className="text-[var(--muted)] font-light  pt-1">
            Tell us who you are and we’ll start your download right away.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-4 space-y-6">
          <div>
            <label className="label  mb-1 block">Name</label>
            <input
              data-testid="download-name"
              className={fieldClass}
              placeholder="Your Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label className="label  mb-1 block">Email</label>
            <input
              data-testid="download-email"
              className={fieldClass}
              placeholder="Your Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="download-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[var(--accent)] text-[#0A0D17] font-mono  tracking-wide uppercase px-6 py-3.5 hover:brightness-105 transition-all disabled:opacity-60"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            {busy ? 'Preparing…' : 'Download now'}
          </button>
          <p className="text-left md:text-center text-[11px] tracking-wide !text-[var(--muted-2)]">
            <Link to="/privacy" className="hover:text-[var(--accent)] underline underline-offset-2 transition-colors">See our Privacy Policy</Link>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DownloadGate;
